// Device storage helpers: the only localStorage keys (theme, sidebar, nav groups, last region, bilingual reading mode) and the dev session flag, all guarded by try/catch.
export const STORAGE_KEYS = {
  theme: "gridcode.theme",
  sidebar: "gridcode.sidebar",
  navGroups: "gridcode.navGroups",
  lastRegion: "gridcode.lastRegion",
  bilingual: "gridcode.bilingual",
} as const;

export const SESSION_KEYS = {
  devSignedOut: "gridcode.devSignedOut",
} as const;

type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];
type SessionKey = (typeof SESSION_KEYS)[keyof typeof SESSION_KEYS];

function local(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function session(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function read(store: Storage | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(store: Storage | null, key: string, value: string | null): void {
  try {
    if (value === null) {
      store?.removeItem(key);
    } else {
      store?.setItem(key, value);
    }
  } catch {
    return;
  }
}

export function readStorage(key: StorageKey): string | null {
  return read(local(), key);
}

export function writeStorage(key: StorageKey, value: string | null): void {
  write(local(), key, value);
}

export function readStorageJson<T>(key: StorageKey, parse: (value: unknown) => T | null): T | null {
  const raw = readStorage(key);
  if (raw === null) {
    return null;
  }
  try {
    return parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeStorageJson(key: StorageKey, value: unknown): void {
  writeStorage(key, JSON.stringify(value));
}

export function readSession(key: SessionKey): string | null {
  return read(session(), key);
}

export function writeSession(key: SessionKey, value: string | null): void {
  write(session(), key, value);
}
